import { getStatusGetQueryKey, useStatusGet } from '@/api/generated';
import type { StatusGet200DataPublicFlags } from '@/api/schemas';

/**
 * Read an anonymous-safe flag from `GET /api/status` `publicFlags`. Exists
 * because `useFlag` rides `GET /api/flags` (`flags:view`, admin-only) and
 * fail-closes for everyone else. Shares the status query cache entry with
 * `useMaintenanceStatus` (same query key) — no extra request. Fail-CLOSED:
 * error/missing → false (hiding a gated page on error is correct; contrast
 * the maintenance kill-switch, which fails open).
 *
 * `isInitialLoad` follows the same latch semantics as `useMaintenanceStatus`:
 * true ONLY before the first settle (success or error), never again — callers
 * use it as a skeleton gate to avoid flashing the coming-soon card while the
 * query is in flight on the first render.
 *
 * Query-error observability is covered by `useMaintenanceStatus`'s one-shot
 * `console.warn` — the always-mounted RootGate keeps that hook live on the
 * same cache entry, so a failing `/api/status` is already surfaced there.
 *
 * The key union derives from the generated contract type, so adding a flag
 * to `publicFlags` in `endpoints/status.ts` auto-extends the accepted keys
 * after `pnpm orval`.
 */
export interface UsePublicFlagResult {
  enabled: boolean;
  isInitialLoad: boolean;
}

export function usePublicFlag(key: keyof StatusGet200DataPublicFlags): UsePublicFlagResult {
  const query = useStatusGet({
    query: {
      queryKey: getStatusGetQueryKey(),
      staleTime: 15_000,
      refetchInterval: 30_000,
      refetchIntervalInBackground: false,
    },
  });
  return {
    // Walk the envelope the same way useMaintenanceStatus does — the mutator
    // unwraps `{ success, data }` so `query.data` is `StatusGet200Data`
    // (`{ maintenance, publicFlags }`). Optional-chain `publicFlags` too: a
    // response missing the object (older API, partial payload) must fail
    // CLOSED to `false`, per this hook's contract — never crash the tree.
    enabled: query.data?.publicFlags?.[key] ?? false,
    // React Query v5 — `isFetched` latches true on the first settle and never
    // reverts; `isLoading && !isFetched` is true only before that first settle.
    isInitialLoad: query.isLoading && !query.isFetched,
  };
}
